import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useAuth } from "@src/presentation/hooks/auth/useAuth";
import { AdminAuthProvider } from "@src/presentation/providers/AdminAuthProvider";
import { Flex, Spin } from "@ir/ui";
import { LoadingSpinner } from "@src/presentation/components/common";

/**
 * Protected Route Component - Presentation layer
 * Single Responsibility: Protects routes that require authentication
 */
interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const auth = useAuth();
  const hasRedirected = useRef(false);
  const isLoading = auth.isLoading();
  const isAuthenticated = auth.isAuthenticated();

  useEffect(() => {
    // Only redirect once and only when not loading and not authenticated
    if (!isLoading && !isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      console.log("Initiating Auth0 login redirect...");
      // Auth0 loginWithRedirect will handle the redirect
      // This will cause a browser redirect, so we don't need to await
      // Use setTimeout to ensure the redirect happens in the next tick
      setTimeout(() => {
        auth
          .login()
          .then(() => {
            console.log("Login redirect initiated successfully");
          })
          .catch((error) => {
            console.error("Failed to redirect to login:", error);
            hasRedirected.current = false; // Reset on error to allow retry
          });
      }, 0);
    }
  }, [auth, isLoading, isAuthenticated]);

  // Show loading while checking authentication
  if (auth.isLoading()) {
    return <LoadingSpinner />;
  }

  // Show redirecting message while Auth0 redirect is in progress
  if (!auth.isAuthenticated()) {
    return (
      <Flex align="center" justifyContent="center" style={{ height: "100vh" }}>
        <Spin tip="Redirecting to login..." spinning={true} />
      </Flex>
    );
  }

  return <AdminAuthProvider>{children}</AdminAuthProvider>;
};
